import { useCallback, useEffect, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import type { DesignElement, TicketDesign } from '@bre-ticketing/ticket-design';

/**
 * The drawing surface (D-89, PART 10).
 *
 * A real canvas: elements are dragged with the pointer, resized from their
 * corners, nudged with the arrow keys, and snapped to a grid unless the shift
 * key says otherwise. What is drawn is a preview of the ticket, not the ticket
 * itself — the truth is the server-side renderer — but it is to scale, in the
 * stock's own inches, with the printable area marked, because a designer needs
 * to see the real size and where the printer cannot reach.
 *
 * Everything here works from the keyboard too: an element can be selected with
 * Tab and moved with the arrow keys, because a mouse is not the only way
 * somebody works.
 */

const HANDLES = ['nw', 'ne', 'sw', 'se'] as const;
type Handle = (typeof HANDLES)[number];

export interface CanvasProps {
  design: TicketDesign;
  values: Record<string, string>;
  selectedId: string | null;
  zoom: number;
  grid: number;
  showGrid: boolean;
  issueIds: Set<string>;
  onSelect: (id: string | null) => void;
  onChange: (elements: DesignElement[]) => void;
  readOnly?: boolean;
}

export function Canvas({ design, values, selectedId, zoom, grid, showGrid, issueIds, onSelect, onChange, readOnly }: CanvasProps) {
  const surface = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState<{ id: string; handle: Handle | 'move'; startX: number; startY: number; original: DesignElement } | null>(null);

  /** Mils to on-screen pixels at the current zoom. 96 CSS pixels to the inch. */
  const px = useCallback((mils: number) => (mils / 1000) * 96 * zoom, [zoom]);
  const toMils = useCallback((pixels: number) => (pixels / (96 * zoom)) * 1000, [zoom]);

  const snap = useCallback((mils: number, free: boolean) => (free || grid <= 0 ? Math.round(mils) : Math.round(mils / grid) * grid), [grid]);

  const move = useCallback(
    (event: PointerEvent) => {
      if (!drag) return;
      const free = event.shiftKey;
      const dx = toMils(event.clientX - drag.startX);
      const dy = toMils(event.clientY - drag.startY);
      const original = drag.original;
      const next = { ...original };
      if (drag.handle === 'move') {
        next.x_mils = snap(original.x_mils + dx, free);
        next.y_mils = snap(original.y_mils + dy, free);
      } else {
        const east = drag.handle.endsWith('e');
        const south = drag.handle.startsWith('s');
        const width = Math.max(50, snap(original.width_mils + (east ? dx : -dx), free));
        const height = Math.max(50, snap(original.height_mils + (south ? dy : -dy), free));
        next.width_mils = width;
        next.height_mils = height;
        if (!east) next.x_mils = snap(original.x_mils + (original.width_mils - width), free);
        if (!south) next.y_mils = snap(original.y_mils + (original.height_mils - height), free);
      }
      onChange(design.elements.map((element) => (element.id === drag.id ? (next as DesignElement) : element)));
    },
    [drag, design.elements, onChange, snap, toMils],
  );

  useEffect(() => {
    if (!drag) return;
    const up = () => setDrag(null);
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
  }, [drag, move]);

  const startDrag = (event: ReactPointerEvent, element: DesignElement, handle: Handle | 'move') => {
    if (readOnly || element.locked) return;
    event.preventDefault();
    event.stopPropagation();
    onSelect(element.id);
    setDrag({ id: element.id, handle, startX: event.clientX, startY: event.clientY, original: { ...element } });
  };

  const nudge = (element: DesignElement, dx: number, dy: number) => {
    if (readOnly || element.locked) return;
    onChange(design.elements.map((other) => (other.id === element.id ? { ...other, x_mils: other.x_mils + dx, y_mils: other.y_mils + dy } : other)));
  };

  const { canvas } = design;

  return (
    <div className="overflow-auto rounded border border-line bg-page p-6">
      <div
        ref={surface}
        onClick={() => onSelect(null)}
        className="relative mx-auto bg-white shadow-sm"
        style={{ width: px(canvas.width_mils), height: px(canvas.height_mils), backgroundColor: canvas.background_hex ?? '#ffffff' }}
      >
        {showGrid ? (
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0"
            style={{
              backgroundImage: 'linear-gradient(to right, rgba(15,23,42,.08) 1px, transparent 1px), linear-gradient(to bottom, rgba(15,23,42,.08) 1px, transparent 1px)',
              backgroundSize: `${px(grid)}px ${px(grid)}px`,
            }}
          />
        ) : null}

        {/* Where the printer cannot reach. */}
        <div
          aria-hidden
          className="pointer-events-none absolute border border-dashed border-accent/40"
          style={{
            left: px(canvas.margins.left_mils),
            top: px(canvas.margins.top_mils),
            width: px(canvas.width_mils - canvas.margins.left_mils - canvas.margins.right_mils),
            height: px(canvas.height_mils - canvas.margins.top_mils - canvas.margins.bottom_mils),
          }}
        />
        {canvas.stub_width_mils ? (
          <div aria-hidden className="pointer-events-none absolute top-0 bottom-0 border-l border-dashed border-ink-300" style={{ left: px(canvas.width_mils - canvas.stub_width_mils) }} />
        ) : null}

        {design.elements.map((element) => (
          <ElementView
            key={element.id}
            element={element}
            values={values}
            px={px}
            selected={element.id === selectedId}
            flagged={issueIds.has(element.id)}
            onPointerDown={(event, handle) => startDrag(event, element, handle)}
            onSelect={() => onSelect(element.id)}
            onNudge={(dx, dy) => nudge(element, dx, dy)}
            readOnly={readOnly ?? false}
          />
        ))}
      </div>
    </div>
  );
}

function ElementView({
  element,
  values,
  px,
  selected,
  flagged,
  onPointerDown,
  onSelect,
  onNudge,
  readOnly,
}: {
  element: DesignElement;
  values: Record<string, string>;
  px: (mils: number) => number;
  selected: boolean;
  flagged: boolean;
  onPointerDown: (event: ReactPointerEvent, handle: Handle | 'move') => void;
  onSelect: () => void;
  onNudge: (dx: number, dy: number) => void;
  readOnly: boolean;
}) {
  const style: React.CSSProperties = {
    left: px(element.x_mils),
    top: px(element.y_mils),
    width: px(element.width_mils),
    height: px(element.height_mils),
    ...(element.rotation ? { transform: `rotate(${element.rotation}deg)`, transformOrigin: 'center' } : {}),
  };

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`${element.name ?? element.kind} at ${(element.x_mils / 1000).toFixed(2)} by ${(element.y_mils / 1000).toFixed(2)} inches`}
      aria-pressed={selected}
      onClick={(event) => {
        event.stopPropagation();
        onSelect();
      }}
      onPointerDown={(event) => onPointerDown(event, 'move')}
      onKeyDown={(event) => {
        const step = event.shiftKey ? 100 : 10;
        if (event.key === 'ArrowLeft') onNudge(-step, 0);
        else if (event.key === 'ArrowRight') onNudge(step, 0);
        else if (event.key === 'ArrowUp') onNudge(0, -step);
        else if (event.key === 'ArrowDown') onNudge(0, step);
        else return;
        event.preventDefault();
      }}
      className={`absolute cursor-move overflow-hidden ${selected ? 'outline-2 outline-accent' : flagged ? 'outline-2 outline-dashed outline-bad' : 'outline-1 outline-dashed outline-transparent hover:outline-ink-300'}`}
      style={style}
    >
      <ElementContent element={element} values={values} px={px} />
      {selected && !readOnly && !element.locked
        ? HANDLES.map((handle) => (
            <span
              key={handle}
              onPointerDown={(event) => onPointerDown(event, handle)}
              className="absolute h-2.5 w-2.5 rounded-sm border border-white bg-accent"
              style={{
                left: handle.endsWith('w') ? -5 : undefined,
                right: handle.endsWith('e') ? -5 : undefined,
                top: handle.startsWith('n') ? -5 : undefined,
                bottom: handle.startsWith('s') ? -5 : undefined,
                cursor: `${handle}-resize`,
              }}
            />
          ))
        : null}
    </div>
  );
}

function ElementContent({ element, values, px }: { element: DesignElement; values: Record<string, string>; px: (mils: number) => number }) {
  switch (element.kind) {
    case 'TEXT':
    case 'FIELD': {
      const raw = element.kind === 'TEXT' ? element.text : `${element.prefix ?? ''}${values[element.field] ?? element.fallback ?? `{${element.field}}`}${element.suffix ?? ''}`;
      const text = element.uppercase ? raw.toUpperCase() : raw;
      return (
        <span
          className="block h-full w-full"
          style={{
            fontFamily: element.font === 'SERIF' ? 'Georgia, serif' : element.font === 'MONO' ? 'ui-monospace, Menlo, monospace' : 'Helvetica, Arial, sans-serif',
            fontSize: px((element.size_pt / 72) * 1000),
            fontWeight: element.weight === 'BOLD' ? 700 : 400,
            lineHeight: element.line_height ?? 1.2,
            textAlign: element.align.toLowerCase() as 'left' | 'center' | 'right',
            color: element.color_hex ?? '#0f172a',
            overflow: 'hidden',
          }}
        >
          {text}
        </span>
      );
    }
    case 'QR':
      return (
        <span className="grid h-full w-full place-items-center bg-[repeating-conic-gradient(#0f172a_0_25%,#ffffff_0_50%)] bg-[length:8px_8px] text-[10px] font-semibold text-white">
          <span className="rounded bg-ink/80 px-1">QR</span>
        </span>
      );
    case 'BARCODE':
      return (
        <span className="grid h-full w-full place-items-center" style={{ background: 'repeating-linear-gradient(to right, #0f172a 0 2px, #ffffff 2px 5px)' }}>
          <span className="rounded bg-white/90 px-1 text-[10px] font-semibold">{element.symbology}</span>
        </span>
      );
    case 'IMAGE':
      return element.file_id ? <img src={`/api/public/media/${element.file_id}`} alt="" className="h-full w-full object-contain" /> : <span className="grid h-full w-full place-items-center bg-mist text-[10px]">image</span>;
    case 'SPONSOR':
      return <span className="grid h-full w-full place-items-center bg-mist text-[10px] text-ink-600">sponsor logo</span>;
    case 'BOX':
      return (
        <span
          className="block h-full w-full"
          style={{
            backgroundColor: element.fill_hex ?? 'transparent',
            border: element.border_hex ? `${Math.max(1, px(element.border_width_mils ?? 10))}px solid ${element.border_hex}` : undefined,
            borderRadius: px(element.corner_radius_mils ?? 0),
          }}
        />
      );
    case 'LINE':
      return <span className="block h-full w-full" style={{ backgroundColor: element.dashed ? 'transparent' : (element.color_hex ?? '#0f172a'), borderTop: element.dashed ? `2px dashed ${element.color_hex ?? '#0f172a'}` : undefined }} />;
    default:
      return null;
  }
}
